import { defineStore } from 'pinia';
import { Workspace, WorkspaceWithProjects } from '@/dashboard/types/workspace';
import { BaseProject, Project } from '@/dashboard/types/project';
import { instance } from '@/dashboard/api/instance';
import { ProjectApi } from '@/dashboard/api/project';
import { WorkspaceApi } from '@/dashboard/api/workspace';
import { LeavedUser } from '@/dashboard/types/leavedUser';
import { AccountApi } from '@/dashboard/api/account';
import { CurrentUser } from '@/dashboard/types/current_user';
import { themeDetect } from '@/dashboard/utils/themeDetect';

type AppStoreState = {
  userId: number;
  currentUser: CurrentUser | null;
  workspaces: WorkspaceWithProjects[];
  workspace: Workspace | undefined | null;
  sharedProjects: Project[];
  project: Project | undefined | null;
  needDeleteProject: BaseProject | null;
  showDeleteWorkspacePopup: boolean;
  leavedUsers: LeavedUser[];
};

export const useAppStore = defineStore('appStore', {
  state: (): AppStoreState => {
    themeDetect();

    return {
      userId: (window as any).g_opt.user.id,
      currentUser: null,
      workspaces: [],
      workspace: null,
      sharedProjects: [],
      project: null,
      needDeleteProject: null,
      showDeleteWorkspacePopup: false,
      leavedUsers: [],
    };
  },
  getters: {
    currentWorkspaceUser: (state: AppStoreState) => state.workspace?.user,
    currentProjectUser: (state: AppStoreState) => state.project?.user,
    baseWorkspaceLink: (state: AppStoreState): string => {
      if (state.workspaces.length > 0) {
        return `/w/${state.workspaces[0].short_link}`;
      }
      return '/';
    },
    baseWorkspaceShortLink: (state: AppStoreState): string | null => {
      if (state.workspaces.length > 0) {
        return state.workspaces[0].short_link;
      }
      return null;
    },
  },
  actions: {
    setCurrentUser(user: CurrentUser) {
      this.currentUser = user;
    },
    async loadCurrentUser() {
      if (this.userId) {
        const data = await AccountApi.getCurrentUser();
        this.setCurrentUser(data.user);
      }
    },

    async loadWorkspaces() {
      const response = await instance.get('/app/workspace/workspaces');
      const workspaces = response.data?.workspaces;
      const sharedProjects = response.data?.shared_projects;

      if (workspaces) {
        this.workspaces = workspaces;
      }
      if (sharedProjects) {
        this.sharedProjects = sharedProjects;
      }
    },
    async loadWorkspaceByShortLink(short_link: string): Promise<Workspace | undefined | null> {
      const result = await WorkspaceApi.getByShortLink(short_link);
      this.workspace = result.workspace;
      return result.workspace;
    },
    async loadWorkspaceById(id: number): Promise<Workspace | undefined | null> {
      const result = await WorkspaceApi.getById(id);
      this.workspace = result.workspace;
      return result.workspace;
    },
    async deleteWorkspace(short_link: string) {
      const formData = new FormData();
      formData.append('short_link', short_link);
      await instance.post('/app/workspace/delete', formData);
    },

    async loadProjectByShortLink(short_link: string): Promise<Project | undefined | null> {
      const result = await ProjectApi.getByShortLink(short_link);
      this.project = result.project;
      this.leavedUsers = result.leaved;
      return this.project;
    },
    async deleteProject(shortLink: string): Promise<boolean> {
      const formData = new FormData();
      formData.append('short_link', shortLink);
      const response = await instance.post(`/app/projects/delete`, formData);

      return !!response.data.success;
    },

    async loadProjectAndWorkspace(projectShortLink: string) {
      const project = await this.loadProjectByShortLink(projectShortLink);
      if (project) {
        await this.loadWorkspaceById(project.workspace_id);
      }
    },

    openDeleteProjectPopup(project: BaseProject) {
      this.needDeleteProject = project;
    },
    closeDeleteProjectPopup() {
      this.needDeleteProject = null;
    },

    openDeleteWorkspacePopup() {
      this.showDeleteWorkspacePopup = true;
    },
    closeDeleteWorkspacePopup() {
      this.showDeleteWorkspacePopup = false;
    },

    setProject(project: Project | null) {
      this.project = project;
    },
    setWorkspace(workspace: Workspace | null) {
      this.workspace = workspace;
    },
  },
});
